// src/components/VisitorCounting.js
import React, { useState, useEffect } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import "./VisitorCounting.css";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

// Helper function to get today's date as YYYY-MM-DD
const getTodayDate = () => new Date().toISOString().split("T")[0];

const VisitorCounting = () => {
  // State for visitor counts
  const [dailyCount, setDailyCount] = useState(0);
  const [yearlyCount, setYearlyCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [lineData, setLineData] = useState([]);

  const apiUrl = process.env.REACT_APP_API_URL; // e.g., http://localhost:5000

  // Function to fetch visitor data from the server
  const fetchVisitorData = () => {
    fetch(`${apiUrl}/api/visitor`)
      .then((res) => res.json())
      .then((data) => {
        setDailyCount(data.dailyCount || 0);
        setYearlyCount(data.yearlyCount || 0);
        setTotalCount(data.totalCount || 0);
        setLineData(data.history || []);
      })
      .catch((err) =>
        console.error("Error fetching visitor data from server:", err)
      );
  };

  // Function to increment visitor count on page load
  const incrementVisitorCount = () => {
    fetch(`${apiUrl}/api/visitor/increment`, {
      method: "POST",
    })
      .then((res) => res.json())
      .then((data) => {
        setDailyCount(data.dailyCount);
        setYearlyCount(data.yearlyCount);
        setTotalCount(data.totalCount);
        setLineData(data.history || []);
      })
      .catch((err) =>
        console.error("Error incrementing visitor data from server:", err)
      );
  };

  useEffect(() => {
    // Increment once when the component mounts
    incrementVisitorCount();

    // Then refresh the visitor data every 5 seconds
    const interval = setInterval(() => {
      fetchVisitorData();
    }, 5000);

    return () => clearInterval(interval);
  }, [apiUrl]);

  // Reset visitor counts by calling the server endpoint
  const handleReset = () => {
    fetch(`${apiUrl}/api/visitor/reset`, {
      method: "POST",
    })
      .then((res) => res.json())
      .then((data) => {
        setDailyCount(data.dailyCount);
        setYearlyCount(data.yearlyCount);
        setTotalCount(data.totalCount);
        setLineData([]);
      })
      .catch((err) =>
        console.error("Error resetting visitor data from server:", err)
      );
  };

  // Prepare chart data using the fetched history
  const chartData = {
    labels: lineData.map((item) => item.date),
    datasets: [
      {
        label: "Daily Visitors",
        data: lineData.map((item) => item.count),
        backgroundColor: "rgba(26, 35, 115, 0.2)",
        borderColor: "rgba(26, 35, 115, 1)",
        borderWidth: 2,
        fill: true,
        tension: 0.3,
      },
    ],
  };

  return (
    <div className="visitor-counting-container">
      <h2>Visitor Counting</h2>
      <div className="count-boxes">
        <div className="count-box">
          <h3>Daily Count</h3>
          <p>{dailyCount}</p>
        </div>
        <div className="count-box">
          <h3>Yearly Count</h3>
          <p>{yearlyCount}</p>
        </div>
        <div className="count-box">
          <h3>Total Count</h3>
          <p>{totalCount}</p>
        </div>
      </div>
      <div className="line-graph">
        <h3>Visitor Trend (Last 7 Days)</h3>
        <Line data={chartData} />
      </div>
      <div className="reset-container">
        <button onClick={handleReset}>Reset Visitor Count</button>
      </div>
    </div>
  );
};

export default VisitorCounting;

